'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Home, Zap, BookOpen, ChevronLeft, ChevronRight, Settings, ClipboardList, FlaskConical } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const navItems = [
  { key: 'nav-home', label: 'Home', icon: Home, path: '/home-page' },
  { key: 'nav-simulator', label: 'Simulator', icon: Zap, path: '/simulator-page' },
  { key: 'nav-practice', label: 'Practice Mode', icon: BookOpen, path: '/practice-mode' },
  { key: 'nav-mock-test', label: 'Mock Test', icon: ClipboardList, path: '/mock-test' },
  { key: 'nav-testing', label: 'Testing & Verification', icon: FlaskConical, path: '/testing-verification' },
];

const bottomItems = [
  { key: 'nav-settings', label: 'Settings', icon: Settings, path: '#' },
];

interface SidebarProps {
  activePath?: string;
}

export default function Sidebar({ activePath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className="flex flex-col bg-white border-r border-border shadow-card flex-shrink-0 transition-all duration-300 ease-in-out"
      style={{ width: collapsed ? 64 : 220 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-3 py-4 border-b border-border min-h-[60px]">
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-base text-foreground tracking-tight truncate">
            WireTutor
          </span>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-3 flex flex-col gap-1 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activePath === item.path;
          return (
            <Link
              key={item.key}
              href={item.path}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-2 py-2.5 rounded-lg transition-all duration-150 group relative
                ${isActive
                  ? 'bg-primary/10 text-primary font-medium' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm truncate">{item.label}</span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="py-3 px-2 border-t border-border flex flex-col gap-1">
        {bottomItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.key}
              href={item.path}
              title={collapsed ? item.label : undefined}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 group relative"
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span className="text-sm truncate">{item.label}</span>}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 w-full mt-1"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!collapsed && <span className="text-sm">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}